import React, { CSSProperties, forwardRef, useEffect, useMemo, useState } from "react";
import { cx } from "@emotion/css";
import { getStaticSpriteUrl } from "../constants";
import { ISaxIconProps, ISaxIconVariant } from "./types";
import { ISaxIconNames } from "./iconNames";
import { getPossibleMatchIcon } from "./getClosestMatch";

// Sprite cache to avoid re-fetching
const spriteCache = new Map<string, Promise<void>>();
const injectedSprites = new Set<string>();

const injectSvgSprite = (spriteUrl: string, variant?: string): Promise<void> => {
  // Extract the sprite file path (without the hash)
  const [spritePath] = spriteUrl.split("#");

  // If already injected, skip
  if (injectedSprites.has(spritePath)) {
    return Promise.resolve();
  }

  // If currently fetching, wait for existing promise
  const existingPromise = spriteCache.get(spritePath);
  if (existingPromise) {
    return existingPromise;
  }

  // Create new fetch promise
  const fetchPromise = (async () => {
    try {
      const response = await fetch(spritePath);
      const svgContent = await response.text();

      // Create container if it doesn't exist
      let container = document.getElementById("isax-svg-sprite-container");
      if (!container) {
        container = document.createElement("div");
        container.id = "isax-svg-sprite-container";
        container.style.display = "none";
        container.setAttribute("aria-hidden", "true");
        document.body.insertBefore(container, document.body.firstChild);
      }

      // Parse and inject the SVG content
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgContent, "image/svg+xml");
      const svgElement = svgDoc.documentElement;

      // Append all children from the sprite SVG, making IDs variant-specific to avoid conflicts
      const containerElement = document.getElementById("isax-svg-sprite-container");
      if (containerElement && variant) {
        Array.from(svgElement.children).forEach(child => {
          const clonedChild = child.cloneNode(true) as Element;
          // Make IDs variant-specific to avoid conflicts between different variant sprites
          if (clonedChild.id) {
            clonedChild.id = `${clonedChild.id}-${variant}`;
          }
          // Also update any internal references to IDs
          const allElements = clonedChild.querySelectorAll("[id]");
          allElements.forEach(el => {
            if (el.id) {
              el.id = `${el.id}-${variant}`;
            }
          });
          containerElement.appendChild(clonedChild);
        });
      } else if (containerElement) {
        // Fallback: append without modification if no variant provided
        Array.from(svgElement.children).forEach(child => {
          containerElement.appendChild(child.cloneNode(true));
        });
      }

      injectedSprites.add(spritePath);
    } catch (error) {
      console.error("Failed to load SVG sprite:", spritePath, error);
      spriteCache.delete(spritePath); // Allow retry on next attempt
    }
  })();

  spriteCache.set(spritePath, fetchPromise);
  return fetchPromise;
};

export const ISaxIcon = forwardRef<SVGSVGElement, ISaxIconProps>((props, ref) => {
  const {
    className: pClassName = "",
    spin = false,
    fade = false,
    beat = false,
    size = 16,
    variant = "Bold",
    iconName: pIconName,
    fill = "currentColor",
    color = "",
    style,
    ...restProps
  } = props;

  const [spriteLoaded, setSpriteLoaded] = useState(false);

  const iconName = ISaxIconNames.includes(pIconName) ? pIconName : getPossibleMatchIcon(pIconName);

  const className = useMemo(
    () =>
      cx("fa svg-inline--fa", {
        [pClassName]: Boolean(pClassName),
        "fa-spin": spin,
        "fa-pulse": beat,
        "fa-fade": fade
      }),
    [beat, fade, pClassName, spin]
  );

  const appliedStyle: CSSProperties = useMemo(() => {
    const appliedStyle: CSSProperties = {
      ...style,
      fontSize: size
    };

    if (color) {
      appliedStyle.fill = color;
      appliedStyle.color = color;
    }

    return appliedStyle;
  }, [color, size, style]);

  const spriteFolder = folderMap[variant];
  const spriteHref = getStaticSpriteUrl(`isax-icon-sprite/${spriteFolder}.svg#${iconName}`);
  const spriteId = `#${iconName}-${spriteFolder}`;

  // Load sprite on mount
  useEffect(() => {
    const loadSprite = async () => {
      await injectSvgSprite(spriteHref, spriteFolder);
      setSpriteLoaded(true);
    };

    loadSprite();
  }, [spriteHref, spriteFolder]);

  // Show nothing while loading
  if (!spriteLoaded) {
    return null; // Or return a loading placeholder if you prefer
  }

  return (
    <svg
      {...restProps}
      className={className}
      fill={fill}
      ref={ref}
      style={appliedStyle}
      viewBox="0 0 512 512"
    >
      <use xlinkHref={spriteId} />
    </svg>
  );
});

const folderMap: Record<ISaxIconVariant, string> = {
  Outline: "outline",
  Bold: "bold",
  TwoTone: "two-tone"
};
